import { getAllFeatureFlagsFlatMap } from "@dransay/feature-flags";
import { getAppSecrets } from "@dransay/secrets";
import { data, redirect, type ShouldRevalidateFunction } from "react-router";
import { parseCartCookie } from "~/features/cart/cart.cookie";
import { GetVendorsFilteredDebugPanel } from "~/features/products/get-vendors-filtered-debug.panel";
import { ProductsLayout } from "~/features/products/layout/products-layout.component";
import i18nServer from "~/tooling/i18n/i18n.server";
import { getCurrentMarket } from "~/tooling/market/get-current-market";
import { getDefaultDeeplinkBaseUrl, VENDOR_PARAMS } from "~/utils/vendor";
import { validateVendorInProductsPage } from "~/utils/vendor-validation.server";
import type { Route } from "./+types/_storefront._products.products";
import { defaultVendorParamsByMarket } from "./helpers/default-vendor-params-by-market";
import { getPageNameHandle } from "./helpers/get-page-name-handle";
import { getProductFilters } from "./helpers/get-product-filters";
import { getProducts } from "./helpers/get-products";
import { isVendorsFilteredAction } from "./helpers/is-vendors-filtered-action";
import { productFormatter } from "./helpers/product-formatter";
import { initProductPageContext } from "./helpers/products-common";

export const shouldRevalidate: ShouldRevalidateFunction = ({
	formAction,
	defaultShouldRevalidate,
}) => {
	// Prevent revalidation for cart-related actions
	if (formAction?.includes("/api/cart")) {
		return false;
	}

	// Prevent revalidation for favorites-related actions
	if (formAction?.includes("/api/favorites")) {
		return false;
	}

	if (isVendorsFilteredAction(formAction)) {
		return false;
	}

	return defaultShouldRevalidate;
};

export const loader = async ({ request }: Route.LoaderArgs) => {
	const { histogram, context, caller } = await initProductPageContext(request);
	const t = await i18nServer.getFixedT(request, "product");
	const searchParams = new URL(request.url).searchParams;
	const { ENVIRONMENT } = await getAppSecrets();

	const vendorId = searchParams.get(VENDOR_PARAMS.vendorId);
	const deliveryMethod = searchParams.get(VENDOR_PARAMS.deliveryMethod);

	const market = await getCurrentMarket();

	if (!vendorId || !deliveryMethod) {
		const url = new URL(request.url);
		const searchParams = new URLSearchParams(url.search);

		const defaultVendorParams = defaultVendorParamsByMarket[market.code];
		const cartCookie = await parseCartCookie(request);

		searchParams.set(
			VENDOR_PARAMS.vendorId,
			vendorId ?? cartCookie?.vendorId ?? defaultVendorParams.vendorId,
		);
		searchParams.set(
			VENDOR_PARAMS.deliveryMethod,
			deliveryMethod ??
				cartCookie?.deliveryMethodKind ??
				defaultVendorParams.deliveryMethod,
		);
		return redirect(`/products?${searchParams.toString()}`, 301);
	}

	const filter = await getProductFilters(request);

	const [enableProductsAutoRefresh, enableGetVendorsFilteredDebug] =
		await getAllFeatureFlagsFlatMap(
			["enableProductsAutoRefresh", "enableGetVendorsFilteredDebug"],
			context.authenticatedUser?.emailAddress,
			context.featureFlagCache,
		);

	const lang = await i18nServer.getLocale(request);

	// Vendor validation must run BEFORE `getProducts` so that an invalid or
	// unnormalized vendor URL is redirected in a single hop. Previously this
	// was parallelized via `Promise.all`, but `getProducts` can itself throw
	// a redirect on the initial request (empty cart + no filters → default-
	// filter redirect), which preempted vendor normalization and forced an
	// extra request carrying the bad vendor query. Running validation first
	// is authoritative — if it redirects or errors, no other fetches are
	// wasted on a request that's about to 301 anyway.
	const validationResult = await validateVendorInProductsPage(
		getDefaultDeeplinkBaseUrl(
			request,
			ENVIRONMENT === "development" ? "http" : "https",
		),
		{
			request,
			vendorId,
			deliveryMethod,
			caller,
			t,
		},
	);

	if ("error" in validationResult) throw validationResult.error;
	if ("redirect" in validationResult) {
		const redirectUrl = new URL(validationResult.redirect);
		// Whitelist of context params carried through the vendor redirect.
		// Copying the whole query string would let arbitrary params leak
		// into canonical 301 targets, so forward only what the destination
		// listing actually consumes (MKP-2035: `search` used to be dropped).
		const paramsToForward = [
			VENDOR_PARAMS.postalCode,
			"search",
			"filters",
		] as const;
		for (const param of paramsToForward) {
			const value = searchParams.get(param);
			if (value) {
				redirectUrl.searchParams.set(param, value);
			}
		}
		return redirect(redirectUrl.toString(), validationResult.status ?? 301);
	}

	// Vendor is now normalized — fan out the remaining data fetches in
	// parallel. The tRPC context is memoised per-request via WeakMap so
	// these all share the same DB pool and auth check.
	//
	// `totalCount` is the count for this vendor/delivery combination
	// before the drawer filters are applied — the denominator in
	// "X of Y products match your search". Computing it on the server
	// prevents the post-hydration text flip that previously caused a
	// layout reflow of the entire grid. We only pay for it when the user
	// has filters beyond vendor/deliveryMethod. The client-side fallback
	// in `product-list.component.tsx` must mirror this scope so back-
	// navigation and hydration agree on the same denominator.
	const hasUserFilters = Object.keys(filter).some(
		(k) => k !== "vendorId" && k !== "deliveryMethodKind",
	);
	const [
		products,
		filterConfig,
		promotedProduct,
		{ items: carouselProducts },
		totalCount,
	] = await Promise.all([
		getProducts({ request, histogram }),
		caller.public.getProductFilterConfig(),
		caller.public.getPromotedProduct({}),
		caller.public.getCuratedProducts({
			filter,
			type: "exclusive",
		}),
		hasUserFilters
			? caller.public
					.getProducts({
						page: 0,
						pageSize: 1,
						filter: {
							vendorId: filter.vendorId,
							deliveryMethodKind: filter.deliveryMethodKind,
						},
					})
					.then((result) => result.metadata.count)
			: Promise.resolve<number | undefined>(undefined),
	]);

	histogram.end(200);

	return data({
		products: products.items,
		carouselProducts: carouselProducts.map((p) => productFormatter(p, lang)),
		count: products.metadata.count,
		totalCount,
		page: products.metadata.currentPage,
		hasMore: products.metadata.hasNextPage,
		useFiltersV2: true,
		promotedProduct: promotedProduct.item
			? productFormatter(promotedProduct.item, lang)
			: undefined,
		enableProductsAutoRefresh,
		enableGetVendorsFilteredDebug,
		filterConfig,
		facets: products.facets,
	});
};

function ProductListPage({ loaderData }: Route.ComponentProps) {
	const {
		products,
		count,
		totalCount,
		filterConfig,
		useFiltersV2,
		enableProductsAutoRefresh,
		enableGetVendorsFilteredDebug,
		facets,
	} = loaderData;

	return (
		<>
			<ProductsLayout
				products={products}
				count={count}
				totalCount={totalCount}
				filterConfig={filterConfig}
				useFiltersV2={useFiltersV2}
				enableProductsAutoRefresh={enableProductsAutoRefresh}
				facets={facets}
			/>
			{enableGetVendorsFilteredDebug && <GetVendorsFilteredDebugPanel />}
		</>
	);
}

export default ProductListPage;

export const handle = {
	...getPageNameHandle("products"),
};
